import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  useMediaQuery,
  Theme,
  Snackbar,
  Alert,
  InputAdornment,
  TextField,
  Grid,
  Card,
  CardContent,
  IconButton,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Groups as GroupsIcon } from '@mui/icons-material';
import { fetchApi } from '../services/api';
import SideMenu from '../components/SideMenu';
import CompanyDetailDialog from '../components/CompanyDetailDialog';
import SearchIcon from '@mui/icons-material/Search';
import { alpha } from '@mui/material/styles';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import BulkUploadCompanyDialog from '../components/BulkUploadCompanyDialog';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmDialog from '../components/ConfirmDialog';

interface Company {
  거래처번호: number;
  업체번호: number;
  거래처명: string;
  담당자명: string;
  대표자: string;
  대표전화: string;
  대표이메일: string;
  기본주소: string;
  상세주소: string;
  거래처로고: string;
}

interface ApiResponse {
  table: Company[];
}

interface SortConfig {
  key: keyof Company | null;
  direction: 'asc' | 'desc' | null;
}

const CompanyListPage: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: null, direction: null });
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const CompanyNo = localStorage.getItem('CompanyNo');
      const response = await fetchApi<ApiResponse>('ITN_S_간편일정거래처', [
        { 컬럼명: '@업체번호', 값: CompanyNo }
      ]);

      if (response.data?.table) {
        setCompanies(response.data.table);
        setTotalCount(response.data.table.length);
      }
    } catch (error) {
      console.error('Failed to fetch companies:', error);
      setSnackbar({
        open: true,
        message: '거래처 목록을 불러오는데 실패했습니다.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const handleAddCompany = () => {
    setSelectedCompany(null);
    setIsDialogOpen(true);
  };

  const handleEditCompany = (company: Company) => {
    setSelectedCompany(company);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedCompany(null);
  };

  const handleSave = (action: string) => {
    fetchCompanies();  // 목록 새로고침
    setSnackbar({
      open: true,
      message: action === 'delete' ? 
        '거래처 삭제가 완료되었습니다.' : 
        '거래처 정보가 저장되었습니다.',
      severity: 'success'
    });
  };

  const handleBulkUploadSuccess = () => {
    fetchCompanies();
    setSnackbar({
      open: true,
      message: '거래처 일괄 등록이 완료되었습니다.',
      severity: 'success'
    });
  };

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  // 정렬된 거래처 목록 계산
  const sortedCompanies = useMemo(() => {
    let sorted = [...companies];
    if (sortConfig.key && sortConfig.direction) {
      sorted.sort((a, b) => {
        if (a[sortConfig.key!] < b[sortConfig.key!]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key!] > b[sortConfig.key!]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    // 검색어로 필터링
    if (searchTerm) {
      sorted = sorted.filter(company => 
        company.거래처명.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.담당자명.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.대표자.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.대표전화.includes(searchTerm) ||
        company.대표이메일.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return sorted;
  }, [companies, sortConfig, searchTerm]);

  // 정렬 핸들러
  const handleSort = (key: keyof Company) => {
    setSortConfig(prevConfig => ({
      key,
      direction: 
        prevConfig.key === key && prevConfig.direction === 'asc' 
          ? 'desc' 
          : 'asc',
    }));
  };

  const handleDelete = async (거래처번호: number) => {
    if (window.confirm('선택한 거래처를 삭제하시겠습니까?')) {
      try {
        await fetchApi('ITN_T_거래처삭제', [{
          컬럼명: '@거래처번호',
          값: 거래처번호
        }]);

        // 삭제 후 데이터 다시 조회
        const response = await fetchApi<{ table: Company[] }>('ITN_S_거래처목록조회', [{
          컬럼명: '@업체번호',
          값: localStorage.getItem('CompanyNo')
        }]);

        if (response.data?.table) {
          setCompanies(response.data.table);
          // 페이지네이션 정보 업데이트
          setTotalCount(response.data.table.length);
          // 현재 페이지에 데이터가 없으면 이전 페이지로 이동
          if (response.data.table.length === 0 && page > 0) {
            setPage(page - 1);
          }
        }
      } catch (error) {
        console.error('Failed to delete company:', error);
      }
    }
  };

  const handleDeleteAllCompanies = async () => {
    try {
      const response = await fetchApi('ITN_T_간편일정거래처삭제', [
        {
          컬럼명: '@업체번호',값: localStorage.getItem('CompanyNo')
        }, 
        {
          컬럼명: '@거래처번호',값: '0'
        }
      ]);

      if (response) {
        setSnackbar({
          open: true,
          message: '모든 거래처가 삭제되었습니다.',
          severity: 'success'
        });
        setCompanies([]);
        // 거래처 목록 새로고침
        // fetchCompanies();
      }
    } catch (error) {
      console.error('거래처 삭제 중 오류 발생:', error);
      setSnackbar({
        open: true,
        message: '거래처 삭제 중 오류가 발생했습니다.',
        severity: 'error'
      });
    }
  };

  const renderMobileContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ py: 2 }}>
        <Paper elevation={0} sx={{ mb: 2, p: 2, mt: 4, backgroundColor: '#fff' }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2
          }}>
            <Typography variant="h6" sx={{ whiteSpace: 'nowrap' }}>거래처 관리</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<UploadFileIcon />}
                onClick={() => setIsBulkUploadOpen(true)}
                sx={{ height: '36px' }}
              >
                거래처 일괄 등록
              </Button>
              <Button
                variant="contained"
                onClick={handleAddCompany}
                sx={{
                  borderRadius: '20px',
                  whiteSpace: 'nowrap',
                  backgroundColor: '#fff',
                  color: 'primary.main',
                  border: '1px solid',
                  borderColor: 'primary.main',
                  height: '36px',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                    color: '#fff'
                  }
                }}
              >
                <AddIcon />
                거래처등록
              </Button>
            </Box>
          </Box>

          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <TextField
              size="small"
              placeholder="검색어를 입력하세요"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                flex: 1,
                backgroundColor: '#fff',
                '& .MuiInputBase-input': {
                  fontSize: '12px',
                  py: 0.5,
                  px: 1,
                  height: '18px'
                }
              }}
            />
            <IconButton
              color="primary"
              size="small"
              sx={{
                p: 0.5,
                borderColor: 'primary.main',
                border: '1px solid',
                borderRadius: '4px',
                backgroundColor: '#fff',
                height: '30px',
                width: '30px'
              }}
            >
              <SearchIcon sx={{ fontSize: '20px' }} />
            </IconButton>
          </Box>
        </Paper>

        <Grid container spacing={1}>
          {companies.map((company) => (
            <Grid item xs={12} key={company.거래처번호} onClick={() => handleEditCompany(company)}>
              <Card sx={{
                height: '100%',
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 3,
                  borderColor: 'primary.main',
                  borderWidth: 1,
                  borderStyle: 'solid'
                }
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="h6">{company.거래처명}</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">담당자: {company.담당자명}</Typography>
                      <Typography variant="body2">대표자: {company.대표자}</Typography>
                    </Box>
                    <Typography variant="body2">연락처: {company.대표전화}</Typography>
                    <Typography variant="body2">이메일: {company.대표이메일}</Typography>
                    <Typography variant="body2" noWrap>주소: {company.기본주소} {company.상세주소}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  const renderPCContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <CircularProgress />
        </Box>
      );
    }

    return (
        <Box sx={{ flex: 1 }}>
        <Paper
          elevation={0}
          sx={{
            ...(isMobile ? { p: 1.5, mt: 6 } : { p: 1.5, m: 1.5, mt: 0 }),
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#fff',
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            height: '62px',
          }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GroupsIcon sx={{ fontSize: 28, color: 'primary.main' }} />
              <Typography variant="h6">거래처 관리</Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
            {/* <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}> */}
            {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}> */}
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteConfirmOpen(true)}
                sx={{ height: '36px' }}
              >
                거래처전체 삭제
              </Button>
            {/* </Box> */}
          {/* </Box> */}
              <Button
                variant="contained"
                startIcon={<UploadFileIcon />}
                onClick={() => setIsBulkUploadOpen(true)}
                sx={{ height: '36px' }}
              >
                거래처 일괄 등록
              </Button>
              <Button 
                variant="contained" 
                startIcon={<AddIcon />} 
                onClick={handleAddCompany}
                sx={{ height: '36px' }}
              >
                거래처 등록
              </Button>
            </Box>

          </Box>
        </Paper>

        <Paper elevation={1} sx={{ p: 1.5, m: 1.5, backgroundColor: '#fff', minHeight: 'calc(100vh - 88px)' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell 
                    onClick={() => handleSort('거래처명')}
                    sx={{
                        height: '42px',
                        padding: '0 16px',
                        width: '300px',
                        minWidth: '300px'
                      }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        height: '100%', 
                        width: '100%'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          거래처명
                          {sortConfig.key === '거래처명' && (
                            sortConfig.direction === 'asc' ? 
                              <ArrowUpwardIcon fontSize="small" /> : 
                              <ArrowDownwardIcon fontSize="small" />
                          )}
                        </Box>
                        <TextField
                        size="small"
                        placeholder="검색..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{
                          ml: 2,
                          fontSize: '14px',
                          backgroundColor: 'white',
                          width: '180px',
                          '& .MuiOutlinedInput-root': {
                            height: '32px',
                            '& fieldset': {
                              borderColor: 'rgba(0, 0, 0, 0.12)',
                            },
                          },
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Box>
                  </TableCell>
                  <TableCell 
                    onClick={() => handleSort('담당자명')}
                    sx={{ cursor: 'pointer' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      담당자
                      {sortConfig.key === '담당자명' && (
                        sortConfig.direction === 'asc' ? 
                          <ArrowUpwardIcon fontSize="small" /> : 
                          <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell 
                    onClick={() => handleSort('대표자')}
                    sx={{ cursor: 'pointer' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      대표자
                      {sortConfig.key === '대표자' && (
                        sortConfig.direction === 'asc' ? 
                          <ArrowUpwardIcon fontSize="small" /> : 
                          <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell 
                    onClick={() => handleSort('대표전화')}
                    sx={{ cursor: 'pointer' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      연락처
                      {sortConfig.key === '대표전화' && (
                        sortConfig.direction === 'asc' ? 
                          <ArrowUpwardIcon fontSize="small" /> : 
                          <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell 
                    onClick={() => handleSort('대표이메일')}
                    sx={{ cursor: 'pointer' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      이메일
                      {sortConfig.key === '대표이메일' && (
                        sortConfig.direction === 'asc' ? 
                          <ArrowUpwardIcon fontSize="small" /> : 
                          <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell 
                    onClick={() => handleSort('기본주소')}
                    sx={{ cursor: 'pointer' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      주소
                      {sortConfig.key === '기본주소' && (
                        sortConfig.direction === 'asc' ? 
                          <ArrowUpwardIcon fontSize="small" /> : 
                          <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedCompanies.map((company) => (
                  <TableRow
                    key={company.거래처번호}
                    hover
                    onClick={() => handleEditCompany(company)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{company.거래처명}</TableCell>
                    <TableCell>{company.담당자명}</TableCell>
                    <TableCell>{company.대표자}</TableCell>
                    <TableCell>{company.대표전화}</TableCell>
                    <TableCell>{company.대표이메일}</TableCell>
                    <TableCell>{`${company.기본주소} ${company.상세주소}`}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <SideMenu
        mobileOpen={mobileOpen}
        handleDrawerToggle={handleDrawerToggle}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - 240px)` },
          ml: { sm: `240px` },
          p: 0,
          display: 'flex',
        }}
      >
        <Box sx={{ flex: 1 }}>

          {isMobile ? renderMobileContent() : renderPCContent()}
        </Box>
      </Box>
      <CompanyDetailDialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        company={selectedCompany}
        onSave={handleSave}
      />
      <BulkUploadCompanyDialog
        open={isBulkUploadOpen}
        onClose={() => setIsBulkUploadOpen(false)}
        onSuccess={handleBulkUploadSuccess}
      />
      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={() => {
          handleDeleteAllCompanies();
          setDeleteConfirmOpen(false);
        }}
        message="모든 거래처를 삭제하시겠습니까? "
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompanyListPage; 